import { genUniqueId } from "@/src/server/generalFunctions/ssr";
import { 
  CompanyDetailsType,
  PassageUserType,
  UserPreferencesType,
  ChatType, 
  ChatSession, 
  PatientFileType, 
  DocumentEmbeddingType, 
  Contact, 
  Memory } from "@/src/shared/contracts/app";

// Empty preferences - stored in separate search-user-preferences index
export const emptyPreferences: UserPreferencesType = {
  userId: "",
  createdDate: 0,
  updatedDate: 0,
  language: "en",
  aiVoice: "TX3LPaxmHKxFdv7VOQHJ",
  micState: "Checking Microphone...",
  aiName: "Zero",
  aiPersonality: "",
  isSpeakerON: true,
  // First login stays typed and quiet. Audio capture, the wake-word model, and
  // spoken greetings begin only after the user explicitly chooses voice.
  isMicrophoneON: false,
  keyboardMuteEnabled: false,
  keyboardMuteKey: "Space",
  disableAutoExitOnInactivity: false,
  componentsMap: [],
  // No timezone: a missing zone stays missing so no guess is ever shown as the
  // user's data. Signup and /api/preferences/timezone capture the real one;
  // clients fall back to the device zone and servers to UTC until then.
};

export const emptyUserInfo: PassageUserType = {
  publicId: "",
  device: "",
  chatId: "",
  isAuthorized: false,
  field: "",
  profession: "",
  dateOfBirth: { Day: 0, Month: 0, Year: 0 },
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  hasNickname: false,
  nickName: "",
  image: "/assets/images/user-profile.png",
  preferences: emptyPreferences,
  sessionTokens: {},
  createdDate: 0,
  role: "AccountOwner",
  environment: "",
  payments: {
    subscriptionStatus: "trialing",
    subscriptionId: "",
  },
  storageUsage: {
    totalStorageBytes: 0,
    totalDocCount: 0,
    lastUpdated: 0,
  },
  memory: "",
  hasBookedMeeting: false,
  meetingDetails: undefined,
  location: null,
};

export const emptyCompanyDetails: CompanyDetailsType = {
  companyName: "",
  companyIndustry: "",
  companyWebsite: "",
  companyAddress: "",
  companyPhoneNumber: "",
  companyId: "",
  staffMembers: [],
  paymentInfo: {
    isRecurring: false,
    taxes: [],
  },
  invoices: [],
  createdDate: 0,
  companyLogoUrl: "/image-placeholder.jpg",
};

// Factory function that creates a new chat object with a fresh UUID each time
export const createEmptyChat = (): ChatType => ({
  chatId: "", // ID to connect all chats from the same session
  reactId: genUniqueId(), //id for rendering - fresh UUID each time
  question: "",
  textAnswer: "",
  audioAnswer: "",
  tokens: 0, //total cost to generate this answer (multiple function calls + query + summary)
  voiceChars: 0, //cost to speak this answer
  updatedDate: 0,
  createdDate: 0,
  fetchedData: [],
  render: "none",
  feedbackStatus: "pending",
  summary: "no summary",
  attachments: [],
  announcements: [],
  isVoiceInput: false,
  voiceDescription: "",
  emotionalAnalysis: {},
  sources: [],
});

// Factory function for partial empty chat
export const createPartialEmptyChat = (): Partial<ChatType> => ({
  chatId: "", // ID to connect all chats from the same session
  reactId: genUniqueId(), //id for rendering - fresh UUID each time
  question: "",
  textAnswer: "",
  audioAnswer: "",
  updatedDate: 0,
  createdDate: 0,
  fetchedData: [],
  render: "none",
  isVoiceInput: false
});

// Keep the old exports for backward compatibility, but use factory functions
export const emptyChat: ChatType = createEmptyChat();
export const partialEmptyChat: Partial<ChatType> = createPartialEmptyChat();


//only used in client to setup the initial structure
export const emptyChatSession: ChatSession = {
  chatHistory: [createEmptyChat()], // Use factory function for fresh UUID
  summary: ""
};

export const emptyPatientFile: PatientFileType = {
  patientUUID: "",
  companyId: "",
  createdDate: 0,
  createdBy: "",
  tokens: 0,
  personalDemographics: {
    name: "",
    lastname: "",
    dateOfBirth: "",
    gender: "",
    address: "",
    phoneNumber: "",
    email: "",
    emergencyContact: {
      name: "",
      phoneNumber: "",
      relationship: "",
    },
    maritalStatus: "",
    occupation: "",
    raceEthnicity: "",
    preferredLanguage: "",
    socialSecurityNumber: "",
    organDonorStatus: "",
    employer: {
      name: "",
      address: "",
      phoneNumber: "",
      email: "",
    },
  },
  insurance: {
    provider: "",
    policyNumber: "",
    policyHolder: {
      name: "",
      address: "",
      phoneNumber: "",
      email: "",
    },
    coverageDetails: "",
    preAuthorization: "",
    coPayDeductible: "",
    secondaryInsurance: "",
  },
  healthcareProviders: {
    primaryCarePhysician: "",
    referringPhysician: "",
    otherSpecialistDoctors: [{ name: "" }],
  },
  medicalHistory: {
    diagnoses: [
      {
        date: "",
        diagnosis: "",
      },
    ],
    surgicalHistory: [
      {
        date: "",
        procedure: "",
      },
    ],
    familyMedicalHistory: [
      {
        relationship: "",
        condition: "",
      },
    ],
    socialHistory: {
      smoking: "",
      alcoholUse: "",
      drugUse: "",
      lifestyle: "",
    },
    medications: [
      {
        medication: "",
        dosage: "",
        frequency: "",
        date: "",
        response: "",
      },
    ],
    allergicReactions: [
      {
        allergy: "",
        reaction: "",
      },
    ],
    womenHealth: {
      pregnancies: 0,
      liveBirths: 0,
      abortions: 0,
      menopausalStatus: "",
    },
  },
  assessmentPlan: {
    visits: [
      {
        date: "",
        reason: "",
        provider: "",
      },
    ],
    results: [
      {
        observation: "",
        temperature: "",
        test: "",
        result: "",
      },
    ],
    treatmentPlan: "",
    followUpRecommendations: "",
    mentalHealthAssessments: "",
  },
  immunizationRecords: {
    vaccines: [
      {
        type: "",
        date: "",
        provider: "",
      },
    ],
  },
  treatmentPlans: {
    current: [
      {
        treatment: "",
        goal: "",
      },
    ],
    past: [
      {
        treatment: "",
        date: "",
        notes: "",
      },
    ],
    progressNotes: [
      {
        treatment: "",
        date: "",
        notes: "",
      },
    ],
  },
  consentsAuthorizations: {
    consentForTreatment: [
      {
        form: "",
        date: "",
        provider: "",
      },
    ],
    privacyNoticeAcknowledgments: [
      {
        form: "",
        date: "",
        provider: "",
      },
    ],
    authorizationReleaseInformation: [
      {
        form: "",
        date: "",
        provider: "",
      },
    ],
    livingWill: "",
    durablePowerOfAttorney: "",
    doNotResuscitate: "",
  },
  billingPayment: {
    servicesRendered: [
      {
        service: "",
        date: "",
        cost: "",
      },
    ],
    paymentsReceived: [
      {
        paymentDate: "",
        amount: "",
      },
    ],
    outstandingBalance: "",
    itemizedBills: [
      {
        service: "",
        date: "",
        cost: "",
      },
    ],
    explanationOfBenefits: "",
    paymentPlanDetails: "",
  },
  laboratoryImagingResults: {
    bloodTests: [
      {
        test: "",
        date: "",
        result: "",
      },
    ],
    urinalysis: [
      {
        test: "",
        date: "",
        result: "",
      },
    ],
    imaging: {
      xraysMRIsCTScans: [
        {
          imagingType: "",
          date: "",
          result: "",
        },
      ],
      ultrasounds: [
        {
          imagingType: "",
          date: "",
          result: "",
        },
      ],
    },
    pathologyReports: [
      {
        test: "",
        date: "",
        result: "",
      },
    ],
  },
  physicalExaminationRecords: {
    weightHeight: {
      weight: "",
      height: "",
    },
    bodyMassIndex: "",
    bloodPressure: "",
    pulseRespiratoryRates: {
      pulse: "",
      respiratoryRate: "",
    },
    generalAppearance: "",
    systemExaminations: {
      cardiovascular: "",
      respiratory: "",
      gastrointestinal: "",
      musculoskeletal: "",
      neurological: "",
      skin: "",
    },
  },
  legalDocuments: {
    procedureConsentForms: [
      {
        form: "",
        date: "",
        provider: "",
      },
    ],
    guardianshipCustodialInfo: "",
    workersCompensationClaims: "",
  },
};

export const industryMapping: Record<string, any> = {
  Medicine: emptyPatientFile,
  Education: emptyPatientFile,
  Finance: emptyPatientFile,
  Construction: emptyPatientFile,
  "Real Estate": emptyPatientFile,
  Retail: emptyPatientFile,
  Hospitality: emptyPatientFile,
  "Legal Services": emptyPatientFile,
  Logistics: emptyPatientFile,
  Telecomunications: emptyPatientFile,
  "Art and Music": emptyPatientFile,
  Environmental: emptyPatientFile,
  Entertainment: emptyPatientFile,
  Marketing: emptyPatientFile,
  Other: emptyPatientFile,
};

export const emptyCustomerFile: any = {};

export const emptyStudentFile: any = {};

export const emptyCalendarFile: any = {};

export const emptyDocumentFile: DocumentEmbeddingType = {
  id: "",
  text: "",
  companyId: "",
  title: "",
  uploadedBy: "",
  fileType: "",
  gpt_query_cosine_search: {
    predicted_value_f32: [],
  },
};

export const emptyMemory: Memory = {
  id: "",
  source: "",
  type: "",
  content: "",
  createdDate: 0,
  priority: 0,
  emotionalIntensity: 0,
  topics: [],
  relationships: {
    primaryContact: "",
    relatedContacts: [],
    relationshipContext: ""
  },
  metadata: {
    confidence: 0,
    sourceObservations: [],
    model: "",
    embeddingVersion: "",
    sourceId: "",
    sourceType: "",
    clusterId: "",
    lastUpdated: 0,
    isHypothesis: false,
    supportCount: 0,
    quality: 0,
    timespan: {
      start: 0,
      end: 0
    }
  },
  gpt_query_cosine_search: {
    predicted_value_f32: []
  },
  companyId: "",
  userId: ""
};

export const emptyContact: Contact = {
  names: {
    displayName: "",
    givenName: "",
    familyName: "",
    middleName: "",
  },
  emailAddresses: [],
  phoneNumbers: [],
  organizations: [],
  addresses: [],
  birthday: {
    year: 0,
    month: 0,
    day: 0,
  },
  photos: [],
  relations: [],
  urls: [],
  interests: [],
  locations: [],
  occupations: [],
  skills: [],
  gender: "",
  contactMemory: [],
  isDeleted: false,
  companyId: "",
  userId: "",
  syncType: "",
  vendor: "",
  lastSynced: 0,
  vendorId: "",
};
